import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Stethoscope, Loader2, Save, Heart, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import RightDrawer from './RightDrawer';
import TriageDictationButton from './TriageDictationButton';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useTenant } from '../contexts/TenantContext';
import { BP, VInput } from './TriageVitalsFields';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * Reception triage: vitals + chief complaint. Saving issues the queue number
 * and emails it to the patient when an address is on file.
 */
export default function TriageDrawer({ open, onClose, queueEntry, onSaved }) {
  const [vitals, setVitals] = useState({});
  const [cc, setCc] = useState('');
  const [saving, setSaving] = useState(false);
  const [transcript, setTranscript] = useState('');
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';

  useEffect(() => {
    if (!open) return;
    const t = queueEntry?.triage;
    setVitals(t?.vitals || {});
    setCc(t?.chief_complaint || '');
    setTranscript('');
  }, [open, queueEntry?.id]);

  if (!open) return null;

  const set = (k, v) => setVitals(prev => ({ ...prev, [k]: v === '' ? undefined : (isNaN(+v) ? v : +v) }));

  const onParsed = (parsed) => {
    // Merge parsed vitals (don't overwrite manually-entered values).
    setVitals(prev => ({ ...(parsed.vitals || {}), ...Object.fromEntries(Object.entries(prev).filter(([_, v]) => v != null && v !== '')) }));
    // Chief complaint: prefer extracted symptoms (comma-joined), fall
    // back to the parser's chief_complaint string. Honours any value the
    // receptionist has already typed.
    if (!cc) {
      if (parsed.symptoms && parsed.symptoms.length > 0) {
        setCc(parsed.symptoms.join(', '));
      } else if (parsed.chief_complaint) {
        setCc(parsed.chief_complaint);
      }
    }
    if (parsed.transcript) setTranscript(parsed.transcript);
  };

  const save = async () => {
    if (!queueEntry?.id) return;
    setSaving(true);
    try {
      const { data } = await ax.patch(`/api/clinic-ops/queue/${queueEntry.id}/triage`, {
        vitals,
        chief_complaint: cc,
        symptoms: cc ? cc.split(/[,;]/).map(s => s.trim()).filter(Boolean).slice(0, 8) : [],
      });
      const qn = data?.queue_no;
      const mailed = data?.email_sent;
      if (qn) {
        toast.success(
          mailed
            ? `Triage saved · Queue #${qn} emailed to patient`
            : `Triage saved · Queue #${qn} issued`,
        );
      } else {
        toast.success(data?.status_label
          ? `Triage saved — ${data.status_label}`
          : 'Triage saved');
      }
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save triage');
    }
    setSaving(false);
  };

  const qLabel = queueEntry?.queue_no
    ? `Queue #${queueEntry.queue_no}`
    : 'Queue pending (issued after save)';

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={`Triage · ${queueEntry?.patient_name || 'Patient'}`}
      subtitle={`${qLabel} · Record vitals & complaint before doctor visit`}
      icon={<Stethoscope className="h-5 w-5 text-white" />}
      accent={brand}
      data-testid="triage-drawer"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: brand }} className="text-white" data-testid="triage-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save triage & issue queue
          </Button>
        </>
      }
    >
      <div className="p-6 space-y-6">
        {/* Voice dictation block */}
        <section className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 flex items-center gap-1.5">
                <MessageSquare className="h-3 w-3" /> Voice dictation
              </div>
              <p className="text-xs text-zinc-600 mt-0.5">Speak the patient conversation — opens a preview so you can review symptoms + vitals before saving. <span className="font-semibold text-violet-700">Tamil, Telugu, Hindi or English</span> all OK.</p>
            </div>
            <TriageDictationButton
              size="md"
              label="Dictate triage"
              onTranscript={setTranscript}
              onParsed={onParsed}
            />
          </div>
          {transcript && (
            <p className="text-xs text-zinc-600 italic bg-white border border-violet-100 rounded p-2 mt-2">
              <span className="font-bold text-violet-700">Heard:</span> {transcript}
            </p>
          )}
        </section>

        {/* Vitals grid */}
        <section>
          <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
            <Heart className="h-3.5 w-3.5 text-rose-500" /> Vital signs
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <BP value={vitals} onChange={set} />
            <VInput label="Pulse" unit="bpm" v={vitals.pulse} on={v => set('pulse', v)} testid="t-pulse" />
            <VInput label="SpO₂" unit="%" v={vitals.spo2} on={v => set('spo2', v)} testid="t-spo2" />
            <VInput label="Temp" unit="°C" v={vitals.temperature} on={v => set('temperature', v)} step="0.1" testid="t-temp" />
            <VInput label="Weight" unit="kg" v={vitals.weight} on={v => set('weight', v)} step="0.1" testid="t-wt" />
            <VInput label="Height" unit="cm" v={vitals.height} on={v => set('height', v)} testid="t-ht" />
            <VInput label="BMI" unit="kg/m²" v={vitals.bmi} on={v => set('bmi', v)} step="0.1" testid="t-bmi" />
            <VInput label="RBS" unit="mg/dL" v={vitals.rbs} on={v => set('rbs', v)} testid="t-rbs" />
          </div>
        </section>

        {/* Chief complaint */}
        <section>
          <Label className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2 block">
            Chief complaint / symptoms
          </Label>
          <Textarea
            rows={4}
            placeholder="e.g. Fever since 2 days, sore throat, body aches"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            data-testid="triage-cc"
            className="resize-none"
          />
          <p className="text-[11px] text-zinc-500 mt-1.5">
            This will appear in the doctor's consultation as a starting point.
          </p>
        </section>
      </div>
    </RightDrawer>
  );
}
